import {
  Avatar,
  Box,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Skeleton,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { SetPasswordModal } from 'BrokerTsLib/src/containers'
import type { DetailedUser } from 'BrokerTsLib/src/dtos'
import { UserType } from 'BrokerTsLib/src/enums'
import { useSendResetPasswordLink } from 'BrokerTsLib/src/hooks/useAccount'
import type { ReactNode } from 'react'

import { HeaderButton } from '@/components'
import { useAuth } from '@/context/AuthContext'
import { House, Key } from '@/icons'

import { Address } from '../../components/Address'
import { EmailAddress } from '../../components/EmailAddress'
import { Phone } from '../../components/Phone'
import { LoggedUserBadge } from '../../components/User/LoggedUserBadge'
import { ValidityBadge } from '../../components/ValidityBadge'
import { UserTransferButton } from './UserTransferButton'

export const UserHeader = ({
  user,
  isLoading,
  children,
}: {
  user?: DetailedUser
  isLoading?: boolean
  children?: ReactNode
}) => {
  const auth = useAuth()
  const toast = useToast()

  const { mutateAsync: resetPassword, isLoading: isResetPasswordLoading } = useSendResetPasswordLink({
    onSuccess: () => { toast({ status: 'success', title: 'E-mail s odkazem pro reset hesla byl zaslán.' }) },
    onError: (error) => { toast({ status: 'error', title: 'Nepodařilo se odeslat e-mail s odkazem pro reset hesla.' }) },
  })

  const fullName = user?.firstName
    ? `${user?.firstName} ${user?.lastName}`
    : user?.userName

  const isCurrentUser = auth?.user?.id === user?.userId

  return (
    <UserHeaderLayout
      avatar={
        <Box position="relative">
          {isCurrentUser && <LoggedUserBadge />}
          <Skeleton isLoaded={!isLoading} borderRadius={8}>
            <Avatar name={fullName!} colorScheme="primary" size={{base: 'md', md:'2xl'}} />
          </Skeleton>
        </Box>
      }
      title={
        <>
          {fullName ? (
            <Heading
              sx={{
                fontSize: { base: '2xl', md: '4xl' },
                fontWeight: 'semibold',
                color: 'white',
              }}
            >
              {fullName}
            </Heading>
          ) : (
            <Skeleton isLoaded={!isLoading} w={56} h={8} />
          )}
          <Flex gap={2}>
            {user?.valid != undefined && (
              <ValidityBadge valid={user?.effectivelyValid} />
            )}
          </Flex>
        </>
      }
      info={
        <Stack alignItems="start">
          {user?.email ? (
            <EmailAddress email={user?.email} showIcon color="white" />
          ) : (
            <Skeleton isLoaded={!isLoading} w={48} h={6} />
          )}
          {user?.phoneNumber ? (
            <Phone number={user?.phoneNumber} mobilePhoneIcon />
          ) : (
            <Skeleton isLoaded={!isLoading} w={48} h={6} />
          )}
          {user?.addressCity &&
            user?.addressStreet &&
            user?.addressStreetNumber &&
            user?.addressPostCode ? (
              <Flex gap={2}>
                <House />
                <Address
                  showIcon
                  city={user?.addressCity}
                  street={user?.addressStreet}
                  streetNumber={user?.addressStreetNumber}
                  postCode={user?.addressPostCode}
                />
              </Flex>
            ) : (
              <Skeleton isLoaded={!isLoading} w={56} h={6} />
            )}
        </Stack>
      }
      buttons={
        <>
          {!isCurrentUser && user && (
            <>
              {auth?.user?.allowEditOrganizationStructure && (
                <UserTransferButton {...user} />
              )}

              {user.userTypeId !== UserType.ActiveDirectoryUser && (
                <HeaderButton
                  onClick={() => resetPassword({ userName: user.userName })}
                  isLoading={isResetPasswordLoading}
                >
                  <Key />
                  Resetovat heslo
                </HeaderButton>
              )}
            </>
          )}
          {auth.user?.adminCebiaRights && (user?.userTypeId === UserType.WsUser || user?.userTypeId === UserType.ApiUser) &&
            <SetPasswordHeaderButton userId={user.userId} />
          }
          {children}
        </>
      }
    />
  )
}

const UserHeaderLayout = ({
  avatar,
  title,
  info,
  buttons,
}: {
  avatar: ReactNode
  title: ReactNode
  info: ReactNode
  buttons: ReactNode
}) => (
  <Box bg="dashboard-header-bg">
    <Container as={Stack} gap={4} py={{base: 4, md: 8}} pt={{base: 6, md: 8}} color="white">
      <Flex
        flexDirection={{ base: 'column', sm: 'row' }}
        justifyContent="space-between"
        gap={4}
      >
        <Grid
          templateColumns="auto 1fr"
          templateRows="auto auto"
          columnGap={{base: 4, md: 6}}
          rowGap={{base: 4, md: 2}}
        >
          <GridItem rowSpan={{ base: 1, sm: 2 }}>{avatar}</GridItem>
          <GridItem alignContent={{base: 'center', md: 'start'}}>{title}</GridItem>
          <GridItem colSpan={{ base: 2, sm: 1 }}>{info}</GridItem>
        </Grid>
        <Flex
          sx={{
            alignItems: 'end',
            flexDir: 'row',
            gap: 2,
          }}
        >
          {buttons}
        </Flex>
      </Flex>
    </Container>
  </Box>
)

const SetPasswordHeaderButton = ({ userId }: { userId: string }) => {
  const disclosure = useDisclosure()

  return <>
    <HeaderButton
      onClick={disclosure.onOpen}
    >
      <Key />
      Změnit heslo
    </HeaderButton>
    <SetPasswordModal {...disclosure} userId={userId} />
  </>
}